import * as React from "react";
import { useResearchChats } from "../../hooks/useChats";
import { useChat } from "../../hooks/useChat";
import { useCreateChat } from "../../hooks/useCreateChat";
import { useDocument } from "../../hooks/useDocument";
const ChatDetail = React.lazy(() => import("./ChatDetail"));
import { Button } from "./ui/button";
import { InputGroup, InputGroupAddon, InputGroupInput } from "./ui/input-group";
import { Loader, ArrowLeft, Search, Plus } from "lucide-react";
const ResearchChatList = React.lazy(() => import("./ResearchChatList"));

interface ResearchProps {
  onNewMessage?: () => void;
}

const Research: React.FC<ResearchProps> = ({ onNewMessage }) => {
  // Get current document context
  const { data: document, isLoading: isLoadingDocument } = useDocument();
  const documentId = document?._id;

  // Fetch chats for the current document
  const { data: chats, isLoading: isLoadingChats, error } = useResearchChats(documentId);

  // Combined loading state
  const isLoading = isLoadingDocument || isLoadingChats;

  const createChatMutation = useCreateChat();

  const [selectedChatId, setSelectedChatId] = React.useState<string | null>(null);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = React.useState("");

  // Load selected chat data
  const { data: selectedChat, isLoading: isLoadingChat } = useChat(selectedChatId || "");

  // Debounce search query
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Filter chats based on debounced search query
  const filteredChats = React.useMemo(() => {
    if (!chats) return [];

    // Reverse chats to show newest first (assuming API returns oldest first)
    const reversedChats = [...chats].reverse();

    if (!debouncedSearchQuery.trim()) return reversedChats;

    return reversedChats.filter((chat) =>
      chat.chat_name.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
    );
  }, [chats, debouncedSearchQuery]);

  // Get the selected chat info from the chats list
  const selectedChatInfo = React.useMemo(() => {
    if (!selectedChatId || !chats) return null;
    return chats.find((chat) => chat.chat_id === selectedChatId);
  }, [selectedChatId, chats]);

  const handleCreateChat = () => {
    createChatMutation.mutate(
      {
        chat_name: "New Chat",
        document_id: documentId,
      },
      {
        onSuccess: (newChat) => {
          setSelectedChatId(newChat.chat_id);
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center flex-1 min-h-0">
        <Loader className="animate-spin" />
        <span className="ml-2">Loading chats...</span>
      </div>
    );
  }

  if (!documentId) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 min-h-0 p-8 text-center">
        <p className="text-sm text-neutral-500">
          No document loaded. Please create or open a document to use research chats.
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 min-h-0 p-8 text-center">
        <p className="text-sm text-danger-200">Failed to load chats. Please try again.</p>
      </div>
    );
  }

  // Render chat detail view
  if (selectedChatId) {
    return (
      <div className="flex flex-col flex-1 min-h-0 relative">
        {/* Header with Back Button */}
        <div className="flex gap-3 items-center mb-1">
          <Button
            variant="ghost"
            className="size-auto p-0"
            size="icon"
            onClick={() => setSelectedChatId(null)}
            title="Back to chat list"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <p className="font-medium text-sm flex-1 truncate">
            {selectedChatInfo?.chat_name || "Loading..."}
          </p>
        </div>

        {/* Chat Detail Content */}
        <div className="flex-1 min-h-0 flex flex-col">
          <React.Suspense
            fallback={
              <div className="flex justify-center items-center h-[200px]">
                <Loader className="animate-spin" />
                <span className="ml-2">Loading...</span>
              </div>
            }
          >
            <ChatDetail
              messages={selectedChat?.prev_chat || []}
              isLoading={isLoadingChat}
              chatId={selectedChatId}
              onNewMessage={onNewMessage}
            />
          </React.Suspense>
        </div>
      </div>
    );
  }

  // Render chat list view
  return (
    <div className="flex flex-col flex-1 min-h-0 gap-3 relative">
      {/* Search Box with Create Button */}
      <div className="flex gap-2 items-center">
        <InputGroup className="flex-1 has-[[data-slot=input-group-control]:focus-visible]:ring-0">
          <InputGroupAddon>
            <Search className="size-4 text-neutral-500" />
          </InputGroupAddon>
          <InputGroupInput
            placeholder="Search chat by name"
            className="placeholder:text-neutral-500"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </InputGroup>
        <Button
          variant="default"
          size="icon"
          onClick={handleCreateChat}
          disabled={createChatMutation.isPending}
          title="Create new chat"
        >
          {createChatMutation.isPending ? (
            <Loader className="size-4 animate-spin" />
          ) : (
            <Plus className="size-4" />
          )}
        </Button>
      </div>
      <div className="flex-1 min-h-0 flex flex-col">
        <React.Suspense
          fallback={
            <div className="flex justify-center items-center h-[200px]">
              <Loader className="animate-spin" />
              <span className="ml-2">Loading...</span>
            </div>
          }
        >
          <ResearchChatList
            chats={filteredChats}
            searchQuery={searchQuery}
            onChatClick={setSelectedChatId}
          />
        </React.Suspense>
      </div>
    </div>
  );
};

export default Research;
